import { IconNameT } from '@cad-arc';
import {
    CadFeatureMap,
    CadNextNotificationEntityEnumType,
    CadNextNotificationTypeEnumType,
    EventPanelFormType,
    EventPanelMetadata,
} from '@mark43/cad-api';
import { FieldPath } from 'react-hook-form';
import { globalAttributes } from '../../../common/global-attributes';
import { AniAliRapidSosTab } from '../ani-ali-e911/ani-ali-rapidsos-tab';
import { CautionsTab } from '../cautions/cautions-tab';
import { DexActivityTab } from '../dex/dex-activity-tab';
import { DispositionsTab } from '../dispositions/dispositions-tab';
import { EventDetailsTab } from '../event-details/event-details-tab';
import { EventLogTab } from '../event-log/event-log-tab';
import { MergedEventsTab } from '../merged-events/merged-events-tab';
import { PersonsTab } from '../persons/persons-tab';
import { ProQaTab } from '../proqa/proqa-tab';
import { RelatedDataTab } from '../related-data/related-data-tab';
import { UnitsTab } from '../units/units-tab';
import { VehiclesTab } from '../vehicles/vehicles-tab';
import { InvolvedPropertiesTab } from '../involved-property/involved-properties-tab';
import { AttachmentsTab } from '../attachments/attachments-tab';

export type EventPanelTab =
    | 'CAUTIONS'
    | 'DEX'
    | 'PERSONS'
    | 'VEHICLES'
    | 'PROPERTY'
    | 'EVENT_DETAILS'
    | 'UNITS'
    | 'ANI_ALI_RAPIDSOS'
    | 'MERGED_EVENTS'
    | 'PROQA'
    | 'DISPOSITIONS'
    | 'EVENT_LOG'
    | 'RELATED_DATA'
    | 'MEDIA_ATTACHMENTS';

type EventPanelTabConfigBase = {
    name: string;
    type: EventPanelTab;
    icon: IconNameT;
    component: React.FC;
    checkEnabled?: (enabledFeatures: CadFeatureMap, eventMetadata?: EventPanelMetadata) => boolean;
    errorFields?: FieldPath<EventPanelFormType>[];
};

/**
 * notificationTypes and notificationsEntityTypes facilitate showing notification counters on tabs
 * and dismissing notifications when a user navigates away from a tab
 */
export type EventPanelTabConfig =
    | (EventPanelTabConfigBase & { notificationTypes?: CadNextNotificationTypeEnumType[] })
    | (EventPanelTabConfigBase & {
          notificationsEntityTypes?: CadNextNotificationEntityEnumType[];
      });

export const eventPanelTabConfigs: Record<EventPanelTab, EventPanelTabConfig> = {
    ANI_ALI_RAPIDSOS: {
        name: 'ANI/ALI',
        type: 'ANI_ALI_RAPIDSOS',
        icon: 'AniAli',
        component: AniAliRapidSosTab,
    },
    CAUTIONS: {
        name: 'Cautions',
        type: 'CAUTIONS',
        icon: 'Alert',
        notificationTypes: ['LOCATION_CAUTION'],
        component: CautionsTab,
    },
    DEX: {
        name: 'DEx',
        type: 'DEX',
        icon: 'Dex',
        checkEnabled: (enabledFeatures, eventMetadata) =>
            enabledFeatures.dataExchangeEventPanelTab &&
            eventMetadata?.agencyTypeGlobalAttrId === globalAttributes.agencyTypeGlobal.POLICE,
        component: DexActivityTab,
    },
    DISPOSITIONS: {
        name: 'Dispositions',
        type: 'DISPOSITIONS',
        icon: 'EventDisposition',
        checkEnabled: (enabledFeatures) => !enabledFeatures.unitLevelDispositionsEnabled,
        component: DispositionsTab,
    },
    EVENT_DETAILS: {
        name: 'Event Details',
        type: 'EVENT_DETAILS',
        icon: 'CADEvent',
        notificationTypes: ['ADDITIONAL_RADIO_CHANNEL', 'ASSOCIATED_EVENT'],
        component: EventDetailsTab,
    },
    EVENT_LOG: {
        name: 'Event Log',
        type: 'EVENT_LOG',
        icon: 'History',
        component: EventLogTab,
    },
    MERGED_EVENTS: {
        name: 'Merged Events',
        type: 'MERGED_EVENTS',
        icon: 'Merge',
        notificationTypes: ['EVENT_MERGED', 'EVENT_UNMERGED'],
        component: MergedEventsTab,
    },
    PERSONS: {
        name: 'Persons',
        type: 'PERSONS',
        icon: 'Person',
        component: PersonsTab,
        notificationsEntityTypes: ['REPORTING_PARTY', 'INVOLVED_PERSON'],
        errorFields: ['reportingParties'],
    },
    PROPERTY: {
        name: 'Involved Property',
        type: 'PROPERTY',
        icon: 'Property',
        checkEnabled: (enabledFeatures) => enabledFeatures.involvedPropertyEnabled,
        notificationsEntityTypes: ['DRUGS', 'FIREARM', 'OTHER_ITEM'],
        component: InvolvedPropertiesTab,
    },
    PROQA: {
        name: 'Pro QA',
        type: 'PROQA',
        icon: 'ProQa',
        checkEnabled: (enabledFeatures) => !enabledFeatures.proQaEnhancementsEnabled,
        component: ProQaTab,
    },
    RELATED_DATA: {
        name: 'Related Data',
        type: 'RELATED_DATA',
        icon: 'Document',
        component: RelatedDataTab,
    },
    UNITS: {
        name: 'Unit Recommendations',
        type: 'UNITS',
        icon: 'Unit',
        notificationTypes: ['EVENT_STAFF_COUNT'],
        component: UnitsTab,
    },
    VEHICLES: {
        name: 'Vehicles',
        type: 'VEHICLES',
        icon: 'Vehicle',
        component: VehiclesTab,
        notificationsEntityTypes: ['VEHICLE', 'TOW_VEHICLE'],
        errorFields: ['vehicles', 'towVehicles'],
    },
    MEDIA_ATTACHMENTS: {
        name: 'Attachments',
        type: 'MEDIA_ATTACHMENTS',
        icon: 'Paperclip',
        component: AttachmentsTab,
        checkEnabled: (enabledFeatures) => enabledFeatures.mediaAttachmentsEnabled,
    },
};

export const newEventPanelTabConfigs: EventPanelTabConfig[] = [
    eventPanelTabConfigs['ANI_ALI_RAPIDSOS'],
    eventPanelTabConfigs['MERGED_EVENTS'],
    eventPanelTabConfigs['RELATED_DATA'],
];

export const externalAgencyEventPanelTabConfigs: EventPanelTabConfig[] = [
    eventPanelTabConfigs['EVENT_DETAILS'],
    eventPanelTabConfigs['CAUTIONS'],
    eventPanelTabConfigs['RELATED_DATA'],
    eventPanelTabConfigs['MEDIA_ATTACHMENTS'],
    eventPanelTabConfigs['PERSONS'],
    eventPanelTabConfigs['VEHICLES'],
    eventPanelTabConfigs['PROQA'],
    eventPanelTabConfigs['DISPOSITIONS'],
    eventPanelTabConfigs['EVENT_LOG'],
];

export const allEventPanelTabConfigs: EventPanelTabConfig[] = [
    eventPanelTabConfigs['EVENT_DETAILS'],
    eventPanelTabConfigs['UNITS'],
    eventPanelTabConfigs['CAUTIONS'],
    eventPanelTabConfigs['DEX'],
    eventPanelTabConfigs['RELATED_DATA'],
    eventPanelTabConfigs['MEDIA_ATTACHMENTS'],
    eventPanelTabConfigs['ANI_ALI_RAPIDSOS'],
    eventPanelTabConfigs['MERGED_EVENTS'],
    eventPanelTabConfigs['PERSONS'],
    eventPanelTabConfigs['VEHICLES'],
    eventPanelTabConfigs['PROPERTY'],
    eventPanelTabConfigs['PROQA'],
    eventPanelTabConfigs['DISPOSITIONS'],
    eventPanelTabConfigs['EVENT_LOG'],
];

export const tabComponents: Record<EventPanelTab, EventPanelTabConfig['component']> =
    Object.fromEntries(
        allEventPanelTabConfigs.map((tabConfig) => [tabConfig.type, tabConfig.component])
    ) as Record<EventPanelTab, EventPanelTabConfig['component']>;

export const scheduledEventPanelTabConfigs: EventPanelTabConfig[] = [
    eventPanelTabConfigs['EVENT_DETAILS'],
    eventPanelTabConfigs['ANI_ALI_RAPIDSOS'],
    eventPanelTabConfigs['PERSONS'],
    eventPanelTabConfigs['VEHICLES'],
];
